import React from "react";
import { createRoot } from "react-dom/client";
import { HashRouter } from "react-router-dom";
import "./tokens.css";
import "./styles.css";
import "./brand.css";
import { applyTheme, getTheme } from "./theme";

applyTheme(getTheme());

/**
 * Start-up. Coming back from Google (or an email link) the URL carries ?code=…;
 * supabase-js exchanges it for a session, we remember who signed in, drop the
 * code from the address bar and reload once so the progress store opens that
 * user's data (it picks its key at load time).
 */
async function boot(): Promise<void> {
  const { supabase, supabaseConfigured } = await import("./supabase");
  const { getProfile, setProfile, userProfile } = await import("./state/profile");
  // Scanned the "use my phone" QR code: trade the one-time code for a session on this phone.
  const qr = /^#\/m\/([A-Za-z0-9_-]{20,64})$/.exec(window.location.hash);
  if (qr && supabaseConfigured()) {
    try {
      const { redeemPhoneCode } = await import("./uploads");
      const r = await redeemPhoneCode(qr[1]);
      const { data, error } = await supabase().auth.verifyOtp({ token_hash: r.tokenHash, type: "magiclink" });
      if (error || !data.user) throw new Error(error?.message ?? "sign-in failed");
      const meta = data.user.user_metadata as { full_name?: string; name?: string };
      setProfile(userProfile(data.user.id, data.user.email ?? r.email, meta.full_name ?? meta.name));
      window.history.replaceState(null, "", `${window.location.pathname}#${r.target}`);
    } catch (e) {
      try {
        sessionStorage.setItem("leanring:login-notice", e instanceof Error ? e.message : String(e));
      } catch {
        /* ignore */
      }
      window.history.replaceState(null, "", `${window.location.pathname}#/`);
    }
    // No reload: the progress store has not been loaded yet (it loads with the app below), so it opens
    // this account's data directly. A reload here could replay the one-time link on iOS Safari.
  }
  const params = new URLSearchParams(window.location.search);
  const returning = params.has("code") || params.has("error_description");
  if (supabaseConfigured() && returning) {
    const { data } = await supabase().auth.getSession();
    const session = data.session;
    const clean = `${window.location.pathname}#/`;
    if (session && getProfile()?.id !== session.user.id) {
      const meta = session.user.user_metadata as { full_name?: string; name?: string };
      const email = session.user.email ?? "";
      setProfile(userProfile(session.user.id, email, meta.full_name ?? meta.name));
      window.location.replace(clean);
      return;
    }
    window.history.replaceState(null, "", clean);
  }
  const { default: App } = await import("./App");
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <HashRouter>
        <App />
      </HashRouter>
    </React.StrictMode>,
  );
}
void boot();
